import React, { useEffect, useState } from 'react'
import {
  BlockingLoader,
  Image,
  Button,
  Container,
  ListContainer,
  TextBody,
  TextTitle,
  TextCaption,
  ToastContext,
  Tools,
  Icon,
  Link
} from '@pmi/pmi-iqos-ds-react'
import {
  analytics,
  cookie,
  parseTranslations,
  PublishedComponent,
  LayoutContainer,
  CommonTranslations,
  htmlTranslation,
  constants,
  Routes,
  goToRoute,
  goTo
} from '@pmi/pmi-iqos-shared-react'
import {
  IqosphereApiManager,
  IqosphereAnalytics
} from '@iqosphere-common/services'
import ProfilePrizesTranslations from './profile-prizes.json'
import format from 'date-fns/format'
import ja from 'date-fns/locale/ja'

const ITEM_LOAD_COUNT = Tools.isDesktop() ? 10 : 5
const VISIBLE_ORDER_STATUS = ['I', 'P', 'E', 'H', 'C'] // Status definitions from https://jira.isobar.ch/browse/PMIRRPJP-47537

const ProfilePrizesComponent = ({ translations, line }) => {
  const popupToast = ToastContext.getContextActions().popupToast
  const [unclaimedPrizes, setUnclaimedPrizes] = useState([])
  const [claimedPrizes, setClaimedPrizes] = useState([])
  const [loadedClaimedPrizes, setLoadedClaimedPrizes] = useState([])
  const [showLoadClaimedPrizes, setShowLoadClaimedPrizes] = useState()
  const [loading, setLoading] = useState(true)

  const api = new IqosphereApiManager(line)

  useEffect(() => {
    const loadData = async () => {
      try {
        const resActions = await api.iqosphereProfile.getPrizes()
        const claimedPrizes = resActions.claimedPrizes
        setUnclaimedPrizes(resActions.unclaimedPrizes)
        setClaimedPrizes(resActions.claimedPrizes)
        setLoadedClaimedPrizes(
          resActions.claimedPrizes.slice(0, ITEM_LOAD_COUNT)
        )
        setShowLoadClaimedPrizes(claimedPrizes.length > ITEM_LOAD_COUNT)
      } catch {
        handleLoadError()
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  const handleLoadError = () => {
    popupToast({
      type: ToastContext.types.error,
      title: translations.tk_errorLoading
    })
  }

  const handleLoadClaimedPrizes = () => {
    const newData = claimedPrizes.slice(
      loadedClaimedPrizes.length,
      loadedClaimedPrizes.length + ITEM_LOAD_COUNT
    )
    setLoadedClaimedPrizes(prevData => [...prevData, ...newData])
    setShowLoadClaimedPrizes(
      loadedClaimedPrizes.length + newData.length < claimedPrizes.length
    )
  }

  const formatDate = date => {
    return format(new Date(date * 1000), constants.DEFAULT_DATE_FORMAT, {
      locale: ja
    })
  }

  const handleAddressConfirmationRedirect = orderId => {
    const route = `${Routes.iqospherePrizeAddressConfirmation}?orderId=${orderId}&callredirectUrl=${Routes.iqosphereProfilePrizes}`
    goToRoute({ mainPath: route })
  }

  const handleViewTicket = () => {
    analytics.clickEvent({
      eventCategory: 'My Account',
      eventLabel: 'View Ticket',
      user_id: cookie.getSpiceId() || ''
    })
    goToRoute({
      mainPath: Routes.iqosphereTokyoSensorium
    })
  }

  const handlePrizeDetail = item => {
    IqosphereAnalytics.myProfilePrizesClick('My Prizes - See Detail')
    goTo(item.prizeVoucherUrl || '')
  }

  const handleViewItinerary = item => {
    IqosphereAnalytics.myProfilePrizesClick(
      `Prize Itinerary - ${item.prizeName}`
    )
    goTo(item.itinerary)
  }

  const handleClickClaimNow = item => {
    IqosphereAnalytics.myProfilePrizesClick(`Prize Receive - ${item.prizeName}`)
    handleAddressConfirmationRedirect(item.orderId)
  }

  return (
    <Container px="5">
      {loading && <BlockingLoader loading />}
      {!loading && (
        <LayoutContainer>
          <Container pb="6" pt={['6', '0']}>
            <TextTitle heading={TextTitle.headings.h2}>
              {translations.tk_profilePrizesTitle}
            </TextTitle>
          </Container>

          <Container>
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_dailyPrizesWonTitle}
            </TextTitle>
            {unclaimedPrizes.length > 0 ? (
              <Container pt="14">
                <ListContainer columns={1} yBetween="4">
                  {unclaimedPrizes.map(item => {
                    return (
                      <Container
                        key={item.orderId}
                        display={Container.displays.flex}
                        divider={Container.dividers.bottom}
                        alignItems={[
                          Container.alignItemsValues.start,
                          Container.alignItemsValues.start,
                          Container.alignItemsValues.center
                        ]}
                        justifyContent={Container.justifyContentValues.between}
                        dividerColor={Container.dividerColors.default}
                        pb="4">
                        <Container px="5">
                          <Image src={item.prizeImage} width="14" />
                        </Container>

                        <Container
                          display={Container.displays.flex}
                          flexDirection={[
                            Container.flexDirections.column,
                            Container.flexDirections.column,
                            Container.flexDirections.row
                          ]}
                          justifyContent={
                            Container.justifyContentValues.between
                          }
                          alignItems={[
                            Container.alignItemsValues.start,
                            Container.alignItemsValues.start,
                            Container.alignItemsValues.center
                          ]}>
                          <Container pr="5">
                            <TextCaption size={TextCaption.sizes.m}>
                              {item.prizeName}
                            </TextCaption>
                          </Container>
                          <Container
                            display={Container.displays.flex}
                            alignItems={Container.alignItemsValues.center}
                            justifyContent={Container.justifyContentValues.end}
                            flexDirection={[
                              Container.flexDirections.column,
                              Container.flexDirections.column,
                              Container.flexDirections.row
                            ]}
                            flexShrink={0}>
                            <Container py="3" pr="12">
                              {formatDate(item.orderDate)}
                            </Container>
                            <Container pr="12">
                              <Button
                                text={translations.tk_claimNow}
                                onClickHandler={() => {
                                  handleClickClaimNow(item)
                                }}
                              />
                              {item.orderSubtype ===
                                constants.ORDER_SUBTYPES.TXU &&
                                item.itinerary && (
                                  <Container
                                    pt="4"
                                    display={Container.displays.flex}
                                    justifyContent={
                                      Container.justifyContentValues.center
                                    }>
                                    <Link
                                      underlined={false}
                                      onClickHandler={() => {
                                        handleViewItinerary(item)
                                      }}>
                                      {translations.tk_itinerary}
                                      <Container
                                        pl="2"
                                        display={
                                          Container.displays.inlineBlock
                                        }>
                                        <Icon image={Icon.images.arrowRight} />
                                      </Container>
                                    </Link>
                                  </Container>
                                )}
                            </Container>
                          </Container>
                        </Container>
                      </Container>
                    )
                  })}
                </ListContainer>
              </Container>
            ) : (
              <Container pt="5">{translations.tk_noPrizesWonText}</Container>
            )}
          </Container>

          <Container pt="14">
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_prizesStatusTitle}
            </TextTitle>
            {claimedPrizes.length > 0 ? (
              <Container pt="14">
                <ListContainer columns={1} yBetween="4">
                  {loadedClaimedPrizes.map(item => {
                    return (
                      <Container
                        key={item.orderId}
                        display={Container.displays.flex}
                        divider={Container.dividers.bottom}
                        dividerColor={Container.dividerColors.default}
                        pb="4">
                        <Container px="5">
                          <Image src={item.prizeImage} width="14" />
                        </Container>
                        <Container
                          display={Container.displays.flex}
                          flexDirection={[
                            Container.flexDirections.column,
                            Container.flexDirections.column,
                            Container.flexDirections.row
                          ]}>
                          <Container>
                            <Container>
                              <TextCaption size={TextCaption.sizes.m}>
                                {item.prizeName}
                              </TextCaption>
                            </Container>
                            <Container display={Container.displays.flex}>
                              <TextCaption size={TextCaption.sizes.m}>
                                {item.orderStatus === 'Y'
                                  ? translations.tk_dateExpiredLabel
                                  : translations.tk_dateClaimedLabel}
                              </TextCaption>
                              <Container pl="2">
                                {item.orderStatus === 'Y'
                                  ? formatDate(item.orderExpiration)
                                  : formatDate(item.orderDate)}
                              </Container>
                            </Container>
                            <Container
                              display={Container.displays.flex}
                              alignItems={Container.alignItemsValues.center}>
                              <TextCaption size={TextCaption.sizes.m}>
                                {translations.tk_statusLabel}
                              </TextCaption>
                              <Container pl="2">
                                <TextBody
                                  {...htmlTranslation(
                                    translations,
                                    `tk_orderStatus_${item.orderStatus}`
                                  )}
                                />
                              </Container>
                            </Container>
                          </Container>
                          <Container
                            display={Container.displays.flex}
                            justifyContent={[
                              Container.justifyContentValues.start,
                              Container.justifyContentValues.start,
                              Container.justifyContentValues.end
                            ]}
                            alignItems={Container.alignItemsValues.center}
                            pr="12"
                            pt={['3', '3', '0']}>
                            <Container>
                              {VISIBLE_ORDER_STATUS.find(
                                status => status === item.orderStatus
                              ) &&
                                item.orderSubtype ===
                                  constants.ORDER_SUBTYPES.TXS && (
                                  <>
                                    <Button
                                      onClickHandler={handleViewTicket}
                                      text={translations.tk_viewTicket}
                                    />
                                  </>
                                )}
                              {item.orderSubtype ===
                                constants.ORDER_SUBTYPES.TXU &&
                                item.itinerary && (
                                  <Container
                                    pt="4"
                                    display={Container.displays.flex}
                                    justifyContent={
                                      Container.justifyContentValues.center
                                    }>
                                    <Link
                                      onClickHandler={() => {
                                        handleViewItinerary(item)
                                      }}
                                      underlined={false}>
                                      {translations.tk_itinerary}
                                      <Container
                                        pl="2"
                                        display={
                                          Container.displays.inlineBlock
                                        }>
                                        <Icon image={Icon.images.arrowRight} />
                                      </Container>
                                    </Link>
                                  </Container>
                                )}
                              {item.orderSubtype === 'PDV' &&
                                item.prizeVoucherUrl && (
                                  <>
                                    <Button
                                      onClickHandler={() => {
                                        handlePrizeDetail(item)
                                      }}
                                      text={translations.tk_prizeDetail}
                                    />
                                  </>
                                )}
                            </Container>
                          </Container>
                        </Container>
                      </Container>
                    )
                  })}
                </ListContainer>
                {showLoadClaimedPrizes && (
                  <Container
                    display={Container.displays.flex}
                    justifyContent={Container.justifyContentValues.center}
                    py="5">
                    <Button
                      variant={Button.variants.secondary}
                      text={translations.tk_loadMore}
                      onClickHandler={() => handleLoadClaimedPrizes()}
                    />
                  </Container>
                )}
              </Container>
            ) : (
              <Container pt="5">
                {translations.tk_noPrizesClaimedText}
              </Container>
            )}
          </Container>
        </LayoutContainer>
      )}
    </Container>
  )
}

const ProfilePrizes = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProfilePrizesComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProfilePrizesTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProfilePrizes',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProfilePrizes.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: () => {}
  })
}

export default ProfilePrizes
